import React, { useState } from "react"

import { Loader2, MessageCircle } from "lucide-react"
import { toast } from "sonner"

import Icon from "@/components/Icon"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Textarea } from "@/components/ui/textarea"
import { useAddComment, useCommentsQuery } from "@/hooks/useComments"

import { PermissionGuard } from "../common/PermissionGuard"

interface CommentsSectionProps {
  applicationId: string
}

const CommentsSection = ({ applicationId }: CommentsSectionProps) => {
  const [comment, setComment] = useState("")

  const {
    data: comments = [],
    isLoading: isLoadingComments,
    error: commentsError,
  } = useCommentsQuery(applicationId)

  const addComment = useAddComment(applicationId)

  const renderComments = () => {
    if (isLoadingComments) {
      return (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          <span className="ml-2 text-sm text-muted-foreground">Loading comments...</span>
        </div>
      )
    }

    if (commentsError) {
      return (
        <div className="flex items-center gap-2 py-4 text-sm text-indicators-red">
          <Icon name="warningCircleFilled" className="h-6 w-6 text-indicators-red" />
          <span>Failed to load comments</span>
        </div>
      )
    }

    if (comments.length === 0) {
      return <p className="text-sm text-muted-foreground">No comments yet</p>
    }

    return comments.map((comment) => (
      <div key={comment.id} className="bg-muted p-3 rounded-md">
        <div className="flex justify-between items-center mb-1">
          <p className="text-sm font-medium">{comment.user?.full_name || "Admin"}</p>
          <p className="text-xs text-muted-foreground">
            {new Date(comment.created_at).toLocaleDateString()}
          </p>
        </div>
        <p className="text-sm">{comment.comment}</p>
      </div>
    ))
  }

  const handleAddComment = () => {
    if (!comment.trim()) {
      toast.error("Comment Required", {
        description: "Please enter a comment before submitting",
      })
      return
    }

    addComment.mutate(comment.trim(), {
      onSuccess: () => {
        setComment("")
        toast.success("Comment Added", {
          description: "Your comment has been saved successfully",
        })
      },
      onError: (err) => {
        toast.error("Failed to Add Comment", {
          description:
            err instanceof Error ? err.message : "There was an error adding your comment",
        })
      },
    })
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-md font-semibold flex items-center gap-2">
          <MessageCircle className="h-4 w-4" />
          Comments
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-3 max-h-[300px] overflow-y-auto pr-1">{renderComments()}</div>

        <PermissionGuard action="insert" scopes={["any", "own"]} resource="comments">
          <div className="space-y-2">
            <h3 className="text-sm font-medium">Add Comment</h3>
            <Textarea
              placeholder="Add a comment about this applicant..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="min-h-[100px] resize-none"
            />
            <Button onClick={handleAddComment} className="w-full" disabled={addComment.isPending}>
              {addComment.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Adding...
                </>
              ) : (
                "Add Comment"
              )}
            </Button>
          </div>
        </PermissionGuard>
      </CardContent>
    </Card>
  )
}

export default CommentsSection
